import { FC, useState } from 'react';

import {
  GiftCouponRedeemStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';

interface RenewalSectionProps {
  sortedCoupons: GiftCouponSummary[];
  onCreateRenewal: (originalCouponId: string, recipientPhone?: string) => Promise<void>;
  onDeleteRenewal: (renewalCouponId: string) => Promise<void>;
  isCreatingRenewal: boolean;
  isDeletingRenewal: boolean;
}

export const RenewalSection: FC<RenewalSectionProps> = ({
  sortedCoupons,
  onCreateRenewal,
  onDeleteRenewal,
  isCreatingRenewal,
  isDeletingRenewal,
}) => {
  const [selectedCouponId, setSelectedCouponId] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const renewableOriginals = sortedCoupons.filter(
    (c) =>
      c.redeemStatus === GiftCouponRedeemStatus.Delivered &&
      !c.isRenewed &&
      !c.isRenewal,
  );

  const renewalCoupons = sortedCoupons.filter(
    (c) => c.isRenewal && c.redeemStatus === GiftCouponRedeemStatus.Available,
  );

  const handleCreate = async () => {
    if (!selectedCouponId) return;
    await onCreateRenewal(selectedCouponId, recipientPhone || undefined);
    setSelectedCouponId('');
    setRecipientPhone('');
    setShowConfirm(false);
  };

  if (renewableOriginals.length === 0 && renewalCoupons.length === 0) return null;

  return (
    <div className="mb-3 shrink-0 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-violet-800">
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        연장쿠폰 발급
      </div>

      {renewableOriginals.length > 0 && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-medium text-gray-600">원본 쿠폰 (발송완료)</label>
            <select
              value={selectedCouponId}
              onChange={(e) => setSelectedCouponId(e.target.value)}
              className="rounded border border-gray-300 px-2 py-1 text-xs focus:border-violet-400 focus:outline-none"
            >
              <option value="">선택하세요</option>
              {renewableOriginals.map((c) => (
                <option key={c._id} value={c._id}>
                  #{c.seq} ({c.code}) - Delivered
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-medium text-gray-600">수령자 전화번호 (선택)</label>
            <input
              type="tel"
              value={recipientPhone}
              onChange={(e) => setRecipientPhone(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="01012345678"
              maxLength={11}
              className="w-[120px] rounded border border-gray-300 px-2 py-1 text-xs focus:border-violet-400 focus:outline-none"
            />
          </div>
          <button
            onClick={() => setShowConfirm(true)}
            disabled={!selectedCouponId || isCreatingRenewal}
            className="rounded bg-violet-600 px-3 py-1 text-xs font-bold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            연장쿠폰 발급
          </button>
        </div>
      )}

      <p className="mt-1 text-[10px] text-violet-700">
        발송 완료된 쿠폰에 대해 유효기간 1년짜리 새 쿠폰을 발급합니다. 원본 쿠폰당 1회만 가능합니다.
      </p>

      {renewalCoupons.length > 0 && (
        <div className="mt-2 border-t border-violet-200 pt-2">
          <p className="text-[10px] font-medium text-violet-700">발급된 연장쿠폰 (삭제 가능)</p>
          <div className="mt-1 flex flex-col gap-1">
            {renewalCoupons.map((c) => (
              <div key={c._id} className="flex items-center gap-2 rounded bg-white px-2 py-1 text-xs">
                <span className="inline-flex items-center rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold text-violet-700">
                  연장
                </span>
                <span className="font-mono text-gray-700">#{c.seq} ({c.code})</span>
                <button
                  onClick={() => onDeleteRenewal(c._id)}
                  disabled={isDeletingRenewal}
                  className="rounded bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700 hover:bg-red-200 disabled:opacity-50"
                >
                  {isDeletingRenewal ? '...' : '삭제'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/30" onClick={() => setShowConfirm(false)} />
          <div className="relative rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-base font-bold text-gray-900">연장쿠폰 발급 확인</h3>
            <p className="mt-2 text-sm text-gray-600">
              선택한 원본 쿠폰에 대해 연장쿠폰을 발급하시겠습니까?
            </p>
            {recipientPhone && (
              <p className="mt-1 text-xs text-gray-500">
                수령자 전화번호: {recipientPhone}
              </p>
            )}
            <p className="mt-1 text-xs text-violet-600">
              유효기간: 발급 시점부터 1년
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowConfirm(false)}
                className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleCreate}
                disabled={isCreatingRenewal}
                className="rounded bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-700 disabled:opacity-50"
              >
                {isCreatingRenewal ? '처리중...' : '발급 실행'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
